import { notifyToast } from "@/components/ui/toast";

/**
 * Minimal shape of what the global handler reads off a React Query mutation.
 * Kept structural so it's trivially unit-testable without a real Mutation.
 */
export interface MutationLike {
  options: {
    onError?: unknown;
    meta?: { silent?: unknown } & Record<string, unknown>;
  };
}

/**
 * Global React Query MutationCache safety net. The app has many mutations; one
 * that fails WITHOUT its own onError used to fail completely silently (the user
 * clicked, nothing happened, no feedback). This surfaces a toast for exactly
 * those cases:
 *   - skip when the mutation defines its own onError (the caller handles it —
 *     no double-report),
 *   - skip when the mutation opts out via meta.silent (deliberately best-effort).
 * Returns true when it surfaced a toast (useful for tests).
 */
export function handleGlobalMutationError(
  error: unknown,
  mutation: MutationLike,
): boolean {
  if (mutation.options.onError) return false;
  if (mutation.options.meta?.silent) return false;
  notifyToast({
    variant: "error",
    title: "Something went wrong",
    description:
      error instanceof Error && error.message ? error.message : "Please try again.",
  });
  return true;
}
